/**
 * SurfaceMesh Component
 *
 * Renders a single building surface as a 3D mesh.
 */

import { useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { ColorScheme, SurfaceGeometry, ViewMode } from '../types';
import { getSurfaceColor, SELECTION_COLOR, HOVER_COLOR } from '../colorSchemes';

interface SurfaceMeshProps {
  surface: SurfaceGeometry;
  colorScheme: ColorScheme;
  viewMode: ViewMode;
  isSelected: boolean;
  onSelect: (id: string, ctrlKey: boolean) => void;
  onDoubleClick?: (id: string) => void;
  showNormals?: boolean;
}

/**
 * Project 3D vertices to 2D plane for Shape creation.
 * Uses the surface normal to determine the projection plane.
 */
function projectTo2D(
  vertices: THREE.Vector3[],
  normal: THREE.Vector3
): { points2D: THREE.Vector2[]; basis: { u: THREE.Vector3; v: THREE.Vector3 }; origin: THREE.Vector3 } {
  if (vertices.length < 3) {
    return { points2D: [], basis: { u: new THREE.Vector3(1, 0, 0), v: new THREE.Vector3(0, 1, 0) }, origin: new THREE.Vector3() };
  }

  // We've already checked vertices.length >= 3
  const [v0, v1] = vertices as [THREE.Vector3, THREE.Vector3];

  // Use the first vertex as origin
  const origin = v0.clone();

  // Create orthonormal basis for the plane
  // u = normalized direction from v0 to v1
  const u = new THREE.Vector3().subVectors(v1, v0).normalize();

  // v = normal x u (perpendicular to both normal and u)
  const v = new THREE.Vector3().crossVectors(normal, u).normalize();

  // Project each vertex onto the 2D plane
  const points2D = vertices.map((vertex) => {
    const relative = new THREE.Vector3().subVectors(vertex, origin);
    return new THREE.Vector2(relative.dot(u), relative.dot(v));
  });

  return { points2D, basis: { u, v }, origin };
}

/**
 * Create a Shape from 2D points.
 */
function createShape(points2D: THREE.Vector2[]): THREE.Shape {
  const shape = new THREE.Shape();

  if (points2D.length === 0) return shape;

  // Use setFromPoints to populate the shape from the array
  shape.setFromPoints(points2D);
  shape.closePath();

  return shape;
}

/**
 * Create a hole Path from 2D points.
 */
function createHole(points2D: THREE.Vector2[]): THREE.Path {
  const hole = new THREE.Path();

  if (points2D.length === 0) return hole;

  // Use setFromPoints to populate the path from the array
  hole.setFromPoints(points2D);
  hole.closePath();

  return hole;
}

export function SurfaceMesh({
  surface,
  colorScheme,
  viewMode,
  isSelected,
  onSelect,
  onDoubleClick,
  showNormals = false,
}: SurfaceMeshProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Convert our Vector3 to Three.js Vector3
  // IDF uses z-up, Three.js uses y-up, so we swap y and z
  const threeVertices = useMemo(
    () => surface.vertices.map((v) => new THREE.Vector3(v.x, v.z, v.y)),
    [surface.vertices]
  );

  const threeNormal = useMemo(
    () => new THREE.Vector3(surface.normal.x, surface.normal.z, surface.normal.y).normalize(),
    [surface.normal]
  );

  // Create the geometry
  const geometry = useMemo(() => {
    if (threeVertices.length < 3) return null;

    // Project vertices to 2D
    const { points2D, basis, origin } = projectTo2D(threeVertices, threeNormal);

    // Create the main shape
    const shape = createShape(points2D);

    // Add holes for fenestrations
    for (const fenestration of surface.fenestrations) {
      // IDF uses z-up, Three.js uses y-up, so we swap y and z
      const fenVertices = fenestration.vertices.map((v) => new THREE.Vector3(v.x, v.z, v.y));
      const { points2D: fenPoints2D } = projectTo2D(fenVertices, threeNormal);
      if (fenPoints2D.length >= 3) {
        const hole = createHole(fenPoints2D);
        shape.holes.push(hole);
      }
    }

    // Create geometry from shape
    const shapeGeometry = new THREE.ShapeGeometry(shape);

    // Transform back to 3D
    const positionAttr = shapeGeometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < positionAttr.count; i++) {
      const x2D = positionAttr.getX(i);
      const y2D = positionAttr.getY(i);

      // Transform 2D point back to 3D
      const point3D = origin
        .clone()
        .add(basis.u.clone().multiplyScalar(x2D))
        .add(basis.v.clone().multiplyScalar(y2D));

      positionAttr.setXYZ(i, point3D.x, point3D.y, point3D.z);
    }

    positionAttr.needsUpdate = true;
    shapeGeometry.computeVertexNormals();

    return shapeGeometry;
  }, [threeVertices, threeNormal, surface.fenestrations]);

  // Calculate centroid for normal arrow
  const centroid = useMemo(() => {
    if (threeVertices.length === 0) return new THREE.Vector3();
    const sum = threeVertices.reduce(
      (acc, v) => acc.add(v),
      new THREE.Vector3()
    );
    return sum.divideScalar(threeVertices.length);
  }, [threeVertices]);

  // Get the color
  const baseColor = useMemo(() => getSurfaceColor(surface, colorScheme), [surface, colorScheme]);

  // Determine the display color based on selection/hover state
  const displayColor = isSelected ? SELECTION_COLOR : isHovered ? HOVER_COLOR : baseColor;

  // Material properties based on view mode
  const materialProps = useMemo(() => {
    switch (viewMode) {
      case 'wireframe':
        return {
          color: displayColor,
          wireframe: true,
          transparent: false,
          opacity: 1,
        };
      case 'x-ray':
        return {
          color: displayColor,
          wireframe: false,
          transparent: true,
          opacity: 0.3,
        };
      case 'shaded':
      default:
        return {
          color: displayColor,
          wireframe: false,
          transparent: true,
          opacity: 0.85,
        };
    }
  }, [viewMode, displayColor]);

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onSelect(surface.id, event.ctrlKey || event.metaKey);
  };

  const handleDoubleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onDoubleClick?.(surface.id);
  };

  if (!geometry) return null;

  return (
    <group>
      <mesh
        ref={meshRef}
        geometry={geometry}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onPointerOver={() => { setIsHovered(true); }}
        onPointerOut={() => { setIsHovered(false); }}
        userData={{ surfaceId: surface.id, type: 'surface', name: surface.name }}
      >
        <meshLambertMaterial
          color={materialProps.color}
          wireframe={materialProps.wireframe}
          transparent={materialProps.transparent}
          opacity={materialProps.opacity}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Edge lines for better visibility */}
      {viewMode === 'shaded' && (
        <lineSegments geometry={new THREE.EdgesGeometry(geometry)}>
          <lineBasicMaterial color="#000000" linewidth={1} />
        </lineSegments>
      )}

      {/* Normal arrow */}
      {showNormals && (
        <arrowHelper
          args={[
            threeNormal,
            centroid,
            2,
            isSelected ? 0xff0000 : 0x00ff00,
            0.5,
            0.3,
          ]}
        />
      )}
    </group>
  );
}
